import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useHistory, useParams } from 'react-router-dom';
import { BlogApis } from '../../apis/BlogApis';
import { BlogCategoryApis } from '../../apis/BlogCategoryApis';
import './style.css';

const API_URL = process.env.REACT_APP_API_URL;

function imgSrc(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
}

function cleanHtml(html) {
    if (!html) return '';
    return html
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/ {2,}/g, ' ');
}

function toSlug(text) {
    return text.toLowerCase().trim()
        .replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-');
}

const EMPTY_FORM = {
    title: '', slug: '', excerpt: '', content: '',
    category_id: '', author_name: '', author_initials: '',
    read_time: '', published_date: new Date().toISOString().split('T')[0],
    thumbnail_src: '', thumbnail_alt: '', desktop_image: '', mobile_image: '',
    status: 'published'
};

export default function BlogForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const history = useHistory();

    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState({});
    const [error, setError] = useState('');
    const [slugManual, setSlugManual] = useState(false);
    const [showSource, setShowSource] = useState(false);
    const quillRef = useRef(null);

    const imageHandler = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            try {
                const fd = new FormData();
                fd.append('image', file);
                const res = await BlogApis.uploadImage(fd);
                const quill = quillRef.current.getEditor();
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', imgSrc(res.data.url));
                quill.setSelection(range.index + 1);
            } catch (err) {
                setError('Image upload failed. Please try again.');
            }
        };
    }, []);

    const QUILL_MODULES = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image']
            ],
            handlers: { image: imageHandler }
        }
    }), [imageHandler]);

    useEffect(() => {
        BlogCategoryApis.listAll().then(res => setCategories(res.data.categories || []));

        if (isEdit) {
            BlogApis.getById(id).then(res => {
                const b = res.data.blog;
                setFormData({
                    title: b.title || '',
                    slug: b.slug || '',
                    excerpt: b.excerpt || '',
                    content: cleanHtml(b.content || ''),
                    category_id: b.category_id || '',
                    author_name: b.author_name || '',
                    author_initials: b.author_initials || '',
                    read_time: b.read_time || '',
                    published_date: b.published_date ? b.published_date.split('T')[0] : '',
                    thumbnail_src: b.thumbnail_src || '',
                    thumbnail_alt: b.thumbnail_alt || '',
                    desktop_image: b.desktop_image || '',
                    mobile_image: b.mobile_image || '',
                    status: b.status || 'published'
                });
                setSlugManual(true);
            }).catch(err => {
                if (err.status === 401) {
                    localStorage.removeItem('token');
                    history.push('/login');
                }
            });
        }
    }, [id, isEdit, history]);

    const set = (key, value) => setFormData(f => ({ ...f, [key]: value }));

    const handleTitleChange = (value) => {
        if (!slugManual) {
            setFormData(f => ({ ...f, title: value, slug: toSlug(value) }));
        } else {
            set('title', value);
        }
    };

    const handleImageUpload = async (field, file) => {
        if (!file) return;
        setUploading(u => ({ ...u, [field]: true }));
        try {
            const fd = new FormData();
            fd.append('image', file);
            const res = await BlogApis.uploadImage(fd);
            set(field, res.data.url);
        } catch (err) {
            setError('Image upload failed. Please try again.');
        } finally {
            setUploading(u => ({ ...u, [field]: false }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const payload = { ...formData, content: cleanHtml(formData.content) };
            if (isEdit) {
                await BlogApis.update(id, payload);
            } else {
                await BlogApis.create(payload);
            }
            history.push('/blogs');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save blog. Please try again.');
        }
    };

    return (
        <div className="dashboard-section blog-form-section">
            <div className="dashboard-section-title">{isEdit ? 'Edit' : 'Add'} Blog Post</div>

            <form className="dashboard-form blog-form" onSubmit={handleSubmit}>
                {error && <p className="form-error">{error}</p>}

                <div className="form-row">
                    <div className="form-group full">
                        <label className="form-label">Title *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.title}
                            onChange={e => handleTitleChange(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group full">
                        <label className="form-label">Slug</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.slug}
                            onChange={e => { setSlugManual(true); set('slug', e.target.value); }}
                            placeholder="auto-generated from title"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group full">
                        <label className="form-label">Excerpt</label>
                        <textarea
                            className="form-input"
                            rows={3}
                            value={formData.excerpt}
                            onChange={e => set('excerpt', e.target.value)}
                            placeholder="Short description shown on listing page"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Category</label>
                        <select
                            className="form-input"
                            value={formData.category_id}
                            onChange={e => set('category_id', e.target.value)}
                        >
                            <option value="">Select category</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status</label>
                        <select
                            className="form-input"
                            value={formData.status}
                            onChange={e => set('status', e.target.value)}
                        >
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Author Name</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.author_name}
                            onChange={e => set('author_name', e.target.value)}
                            placeholder="e.g. Alex Kumar"
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Author Initials</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.author_initials}
                            onChange={e => set('author_initials', e.target.value)}
                            placeholder="e.g. AK"
                            maxLength={5}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Read Time</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.read_time}
                            onChange={e => set('read_time', e.target.value)}
                            placeholder="e.g. 8 min read"
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Published Date</label>
                        <input
                            type="date"
                            className="form-input"
                            value={formData.published_date}
                            onChange={e => set('published_date', e.target.value)}
                        />
                    </div>
                </div>

                {/* Thumbnail image */}
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Thumbnail Image</label>
                        <small className="image-size-hint">Required size: 358 × 219px</small>
                        <input
                            type="file"
                            accept="image/*"
                            className="file-input"
                            onChange={e => handleImageUpload('thumbnail_src', e.target.files[0])}
                        />
                        {uploading.thumbnail_src && <span className="upload-status">Uploading...</span>}
                        {formData.thumbnail_src && (
                            <img
                                src={imgSrc(formData.thumbnail_src)}
                                alt="thumbnail preview"
                                className="image-preview"
                            />
                        )}
                    </div>
                    <div className="form-group">
                        <label className="form-label">Thumbnail Alt Text</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.thumbnail_alt}
                            onChange={e => set('thumbnail_alt', e.target.value)}
                            placeholder="Describe the image"
                        />
                    </div>
                </div>

                {/* Hero images */}
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Desktop Hero Image</label>
                        <small className="image-size-hint">Required size: 834 × 550px</small>
                        <input
                            type="file"
                            accept="image/*"
                            className="file-input"
                            onChange={e => handleImageUpload('desktop_image', e.target.files[0])}
                        />
                        {uploading.desktop_image && <span className="upload-status">Uploading...</span>}
                        {formData.desktop_image && (
                            <img
                                src={imgSrc(formData.desktop_image)}
                                alt="desktop preview"
                                className="image-preview"
                            />
                        )}
                    </div>
                    <div className="form-group">
                        <label className="form-label">Mobile Hero Image</label>
                        <small className="image-size-hint">Required size: 834 × 550px</small>
                        <input
                            type="file"
                            accept="image/*"
                            className="file-input"
                            onChange={e => handleImageUpload('mobile_image', e.target.files[0])}
                        />
                        {uploading.mobile_image && <span className="upload-status">Uploading...</span>}
                        {formData.mobile_image && (
                            <img
                                src={imgSrc(formData.mobile_image)}
                                alt="mobile preview"
                                className="image-preview"
                            />
                        )}
                    </div>
                </div>

                {/* Rich text editor */}
                <div className="form-row">
                    <div className="form-group full">
                        <div className="editor-label-row">
                            <label className="form-label">Content</label>
                            <button
                                type="button"
                                className="source-toggle-btn"
                                onClick={() => setShowSource(s => !s)}
                            >
                                {showSource ? 'Visual Editor' : 'Source Code'}
                            </button>
                        </div>
                        {showSource ? (
                            <textarea
                                className="form-input source-code-area"
                                value={formData.content}
                                onChange={e => set('content', e.target.value)}
                                spellCheck={false}
                            />
                        ) : (
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={formData.content}
                                onChange={val => set('content', val)}
                                modules={QUILL_MODULES}
                                className="blog-editor"
                            />
                        )}
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="form-submit">
                        {isEdit ? 'Update' : 'Create'} Blog
                    </button>
                    <button type="button" className="form-cancel" onClick={() => history.push('/blogs')}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}
